import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

export interface CategoryOption {
  value: string;
  label: string;
}

/**
 * Buscador tipo "autocomplete" para elegir categorías: se escribe para
 * filtrar, se hace click (o Enter) para agregar, y las ya elegidas se
 * muestran como chips removibles debajo. Respeta un máximo configurable.
 */
@Component({
  selector: 'app-category-autocomplete',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="cat-ac">
      <div class="cat-ac-control" [class.invalid]="invalid">
        <i class="bi bi-search" aria-hidden="true"></i>
        <input placeholder="Busca o elige una categoría"
          #searchInput
          type="text"
          [(ngModel)]="searchText"
          (focus)="open()"
          (blur)="onBlur()"
          (input)="onType()"
          (keydown)="onKeydown($event)"
          [disabled]="selected.length >= max"
          [placeholder]="selected.length >= max ? 'Límite de ' + max + ' alcanzado' : placeholder"
          autocomplete="off"
        >
      </div>
      <div class="cat-ac-panel" *ngIf="isOpen() && selected.length < max">
        <button
          type="button"
          *ngFor="let option of filtered(); let i = index; trackBy: trackOption"
          class="cat-ac-option"
          [class.active]="i === activeIndex()"
          (mousedown)="$event.preventDefault(); pick(option)"
        >{{ option.label }}</button>
        <p class="cat-ac-empty" *ngIf="!filtered().length">Sin coincidencias.</p>
      </div>
      <div class="cat-ac-chips" *ngIf="selected.length">
        <span class="cat-chip" *ngFor="let value of selected">
          {{ labelFor(value) }}
          <button type="button" (click)="remove(value)" [attr.aria-label]="'Quitar ' + labelFor(value)"><i class="bi bi-x-lg"></i></button>
        </span>
      </div>
      <div class="cat-ac-status">
        <small class="cat-ac-counter" [class.limit]="selected.length >= max">{{ selected.length }} de {{ max }} categorías seleccionadas</small>
        <button type="button" class="cat-ac-clear" [disabled]="!selected.length" (click)="clear()">Quitar todas</button>
      </div>
    </div>
  `,
  styles: [`
    .cat-ac{position:relative;display:block}
    .cat-ac-control{position:relative;display:flex;align-items:center;gap:.55rem;box-sizing:border-box;width:100%;min-height:48px;padding:0 .8rem;border:1px solid var(--ts-border-strong,rgba(148,173,207,.28));border-radius:9px;background:var(--ts-input-bg,#071a31);transition:border-color .15s ease,box-shadow .15s ease}
    .cat-ac-control:focus-within{border-color:var(--ts-primary,#2f7cff);box-shadow:0 0 0 3px var(--ts-primary-soft-14,rgba(47,124,255,.14))}
    .cat-ac-control.invalid{border-color:var(--ts-danger,#f05259)}
    .cat-ac-control>i:first-child{color:var(--ts-placeholder,#71849e);font-size:.9rem}
    .cat-ac-control input{flex:1;min-width:0;height:46px;margin:0;padding:0;border:0 !important;outline:0 !important;box-shadow:none !important;background:transparent !important;color:var(--ts-text,#f4f8ff);font:inherit;font-size:.9rem}
    .cat-ac-control input::placeholder{color:var(--ts-placeholder,#71849e)}
    .cat-ac-control input:disabled{cursor:not-allowed}
    .cat-ac-panel{position:absolute;z-index:30;top:calc(100% + 6px);left:0;right:0;max-height:280px;overflow-y:auto;box-sizing:border-box;padding:.4rem;border:1px solid var(--ts-border-strong,rgba(148,173,207,.28));border-radius:11px;background:var(--ts-bg-elevated,#0a1c34);box-shadow:var(--ts-shadow-md,0 14px 36px rgba(0,7,22,.24))}
    .cat-ac-option{display:block;width:100%;box-sizing:border-box;padding:.6rem .7rem;border:0;border-radius:8px;background:transparent;color:var(--ts-text,#f4f8ff);font-size:.86rem;text-align:left;cursor:pointer}
    .cat-ac-option:hover,.cat-ac-option.active{background:var(--ts-primary-soft-14,rgba(47,124,255,.14))}
    .cat-ac-empty{margin:0;padding:.5rem .7rem;color:var(--ts-text-muted,#91a4bf);font-size:.8rem}
    .cat-ac-chips{display:flex;flex-wrap:wrap;gap:.32rem;margin-top:.55rem}
    .cat-chip{display:inline-flex;align-items:center;gap:.28rem;padding:.2rem .32rem .2rem .55rem;border:1px solid var(--ts-primary-border-45,rgba(116,160,255,.45));border-radius:999px;background:var(--ts-primary-soft-14,rgba(47,124,255,.14));color:var(--ts-text,#f4f8ff);font-size:.7rem;font-weight:600;line-height:1.5}
    .cat-chip button{display:grid;place-items:center;width:14px;height:14px;border:0;border-radius:50%;padding:0;color:var(--ts-text-muted,#91a4bf);background:transparent;font-size:.55rem;cursor:pointer}
    .cat-chip button:hover{color:var(--ts-text-on-accent);background:var(--ts-danger,#f05259)}
    .cat-ac-status{display:flex;align-items:center;justify-content:space-between;gap:1rem;margin-top:.55rem}
    .cat-ac-counter{color:var(--ts-text-muted,#91a4bf);font-size:.75rem;font-weight:700}
    .cat-ac-counter.limit{color:var(--ts-warning-text,#ffd77d)}
    .cat-ac-clear{padding:.2rem .4rem;border:0;background:transparent;color:var(--ts-danger-text,#ffadb1);font-size:.72rem;font-weight:750;cursor:pointer}
    .cat-ac-clear:disabled{opacity:.4;cursor:not-allowed}
  `],
})
export class CategoryAutocompleteComponent {
  @Input() options: CategoryOption[] = [];
  @Input() selected: string[] = [];
  @Input() max = 8;
  @Input() invalid = false;
  @Input() placeholder = 'Buscar categoría…';
  @Output() selectedChange = new EventEmitter<string[]>();

  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;

  searchText = '';
  isOpen = signal(false);
  activeIndex = signal(-1);

  filtered(): CategoryOption[] {
    const term = this.searchText.trim().toLowerCase();
    const available = this.options.filter(option => !this.selected.includes(option.value));
    if (!term) return available;
    return available.filter(option => option.label.toLowerCase().includes(term) || option.value.toLowerCase().includes(term));
  }

  labelFor(value: string): string {
    return this.options.find(option => option.value === value)?.label || value;
  }

  open(): void {
    if (this.selected.length >= this.max) return;
    this.isOpen.set(true);
    this.activeIndex.set(-1);
  }

  onType(): void {
    this.open();
  }

  onBlur(): void {
    this.isOpen.set(false);
    this.searchText = '';
  }

  pick(option: CategoryOption): void {
    if (this.selected.length >= this.max || this.selected.includes(option.value)) return;
    this.selectedChange.emit([...this.selected, option.value]);
    this.searchText = '';
    this.activeIndex.set(-1);
  }

  remove(value: string): void {
    this.selectedChange.emit(this.selected.filter(item => item !== value));
  }

  clear(): void {
    this.selectedChange.emit([]);
  }

  onKeydown(evt: KeyboardEvent): void {
    const options = this.filtered();
    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      this.open();
      this.activeIndex.set(Math.min(this.activeIndex() + 1, options.length - 1));
    } else if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      this.activeIndex.set(Math.max(this.activeIndex() - 1, 0));
    } else if (evt.key === 'Enter') {
      evt.preventDefault();
      const option = options[this.activeIndex()];
      if (option) this.pick(option);
    } else if (evt.key === 'Escape') {
      this.isOpen.set(false);
    } else if (evt.key === 'Backspace' && !this.searchText && this.selected.length) {
      this.remove(this.selected[this.selected.length - 1]);
    }
  }

  trackOption(_: number, option: CategoryOption): string {
    return option.value;
  }

  focusSearch(): void {
    const target = this.searchInputRef?.nativeElement;
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('field-error-flash');
    target.focus();
    setTimeout(() => target.classList.remove('field-error-flash'), 1900);
  }
}
